import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { every, filter as lodashFilter, includes } from 'lodash-es';
import { AuthenticationService } from '../../../../core/services/authentication.service';
import { OauthProvider } from '../../../../oauth/providers';
import { RequestBodyService } from '../../../services';
import { CommonUtilService } from '../../../services/CommonUtil.service';
import { ReConnectSettingComponent } from '../../re-connect-setting/re-connect-setting.component';
import { AccountAttributesEnum } from '../../../enums';
import { UserService } from '../../../../core/services/user.service';
import { ZohoApiService } from '../../../services/api/zoho-api.service';
import { getErrorMessage } from 'src/app/shared/helpers';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormsModule } from '@angular/forms';
import { SiModalModule } from '../../si-modal';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { FilterPipeModule, OrderByPipeModule } from '../../../pipes';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  DuplicateResolutionRadioGroupModule
} from '../../ui-components/duplicate-resolution-group/duplicate-resolution-radio-group.module';

@UntilDestroy()
@Component({
    selector: 'app-zoho',
    templateUrl: './zoho.component.html',
    styleUrls: ['./../export.component.scss'],
    imports: [
      CommonModule, FormsModule, SiModalModule,
      MatProgressSpinnerModule, MatFormFieldModule,
      FontAwesomeModule, DuplicateResolutionRadioGroupModule,
      MatExpansionModule, FilterPipeModule, OrderByPipeModule,
      MatInputModule, MatButtonModule
    ],
    standalone: true,
})
export class ZohoComponent implements OnInit {
    pages;
    leadOwners;
    campaigns;
    exportType = '';
    generalSettings;
    showPreviousButton = false;
    showNextButton = false;
    currentPage;
    initialPage;
    selectedLeadOwner;
    selectedCampaigns = [];
    contacts = [];
    companyData = [];
    icpIntelJobUUID: string = null;
    duplicateLeadsMatchIds = {};
    duplicateContactsMatchIds = [];
    duplicateLeadsCount = 0;
    duplicateContactsCount = 0;
    duplicateMatchIds;
    duplicateCount = 0;
    duplicateResolution = 'update';
    exportedContactCount = 0;
    busyLabel = 'Loading ...';
    busySublabel = '';
    isBusy = false;
    showCloseButton = false;
    showCancelButton = true;
    showRetryButton = false;
    failedContacts: FailedContactModel[] = [];
    duplicateFailedContacts: FailedContactModel[] = [];
    exportError = '';
    filterLeadOwner = '';
    filterCampaigns = '';
    pageArray;
    duplicateCompanyMatchIds = [];
    duplicateCompanyCount = 0;
    exportedCompanyCount = 0;
    failedCompany = [];
    isZohoComponentOpened: boolean = true;
    accuracyTier: string;
    accuracyTierText: string;
    readonly accountAttributesEnum: typeof AccountAttributesEnum = AccountAttributesEnum;

    constructor(
        private commonUtilService: CommonUtilService,
        private authenticationService: AuthenticationService,
        private readonly matDialog: MatDialog,
        private readonly matDialogRef: MatDialogRef<ZohoComponent>,
        private requestBodyService: RequestBodyService,
        private zohoApiService: ZohoApiService,
        private userService: UserService,
        @Inject(MAT_DIALOG_DATA) public readonly data: any,
    ) {
        this.icpIntelJobUUID = this.data['icpIntelJobUUID'] || null;

        if (this.data['contacts']) {
            this.contacts = this.data['contacts'];
            this.pages = {
                loading: 0,
                exportType: 1,
                leadOwner: 2,
                campaign: 3,
                duplicates: 4,
                export: 5,
                exportFinished: 6,
                exportEmpty: 7,
                exportFailed: 8,
                requiresReauth: 9
            };
            this.accuracyTier = this.data['accuracyTier'];
            this.accuracyTierText = this.data['accuracyTierText'];
        } else if (this.data['company']) {
            this.companyData = this.data['company'];
            this.pages = {
                loading: 0,
                duplicates: 1,
                export: 2,
                exportFinished: 3,
                exportEmpty: 4,
                exportFailed: 5,
                requiresReauth: 6
            };
        }
    }

    ngOnInit() {
        this.initializeFirstStep();
    }

    initializeFirstStep() {
        this.currentPage = this.pages.loading;
        this.initialPage = this.currentPage;
        this.zohoApiService.validateMapping().pipe(untilDestroyed(this)).subscribe((mappingData: any) => {
            if (Object.values(mappingData).every(item => item === true)) {
                this.getSettings();
            } else {
                const sectionsInError = [];
                if (this.data['contacts']) {
                    if (!mappingData.leadFields) {
                        sectionsInError.push('Lead Mappings');
                    }
                    if (!mappingData.contactFields) {
                        sectionsInError.push('Contact Mappings');
                    }
                    if (!mappingData.accountFields) {
                        sectionsInError.push('Company Mappings');
                    }
                    if (this.userService.hasAccess(this.accountAttributesEnum.TECHEXPORTS)) {
                        if (!mappingData.technoLeadFields) {
                            sectionsInError.push('Technographics Mappings');
                        }
                        if (!mappingData.technoContactFields) {
                            sectionsInError.push('Technographics Mappings');
                        }
                        if (!mappingData.technoAccountFields) {
                            sectionsInError.push('Technographics Mappings');
                        }
                    }
                } else {
                    if (!mappingData.accountFields) {
                        sectionsInError.push('Company Mappings');
                    }
                    if (this.userService.hasAccess(this.accountAttributesEnum.TECHEXPORTS) && !mappingData.technoAccountFields) {
                        sectionsInError.push('Technographics Mappings');
                    }
                }
                const uniqueSections = Array.from(new Set(sectionsInError));
                uniqueSections.forEach(section => {
                    this.commonUtilService.showToastWithError(
                        `One or more fields in your ${section} are no longer available. Update your ${section}.`
                    );
                });
                this.matDialogRef.close();
            }
        }, error => {
            this.zohoApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(data => {
                this.initializeFirstStep();
            }, refreshError => {
                this.errorRefreshToken();
            });
        });
    }

    errorRefreshToken() {
        if (this.isZohoComponentOpened) {
            this.onClose();
            const modalConfig: MatDialogConfig = {
                panelClass: 'modal-lg',
                data: {}
            };
            if (this.data['contacts']) {
                modalConfig.data['provider'] = OauthProvider.Zoho;
                modalConfig.data['match_ids'] = this.contacts;
            } else {
                modalConfig.data['provider'] = OauthProvider.ZohoCompany;
                modalConfig.data['match_ids'] = this.companyData;
                modalConfig.data['ifFromList'] = this.commonUtilService.ifFromList;
            }
            this.matDialog.open(ReConnectSettingComponent, modalConfig);
        }
    }

    getSettings() {
        this.zohoApiService.getSettings().pipe(untilDestroyed(this)).subscribe((response) => {
            this.generalSettings = response.general;
            if (this.data['contacts']) {
                this.loadLeadOwners();
            } else if (this.data['company']) {
                this.validateCompanyDuplicates();
            }
        }, error => {
            if (error.status === 404 && error.error?.message) {
                this.commonUtilService.showToastWithError(getErrorMessage(error));
                this.onClose();
                return;
            }
            this.zohoApiService.refreshToken().pipe(untilDestroyed(this)).subscribe(() => {
                this.ngOnInit();
            }, refreshError => {
                if (refreshError.status === 401) {
                    this.errorRefreshToken();
                }
            });
        });
    }

    loadLeadOwners() {
        this.zohoApiService.loadLeadOwners().pipe(untilDestroyed(this)).subscribe((response: any) => {
            const userEmail = this.authenticationService.getEmailAndName('zhEmail', 'zhDisplayName')['email'].toLowerCase();
            this.leadOwners = response.items;
            this.leadOwners.forEach((element) => {
                if (element.email === userEmail) {
                    if (this.generalSettings.defaultOwnerIsConnectedUser === true) {
                        this.selectedLeadOwner = element.id;
                    }
                }
            });
            if (this.generalSettings.promptForCampaigns) {
                this.loadCampaigns();
            } else {
                this.updatePageSequence();
            }
        }, (error) => {
            if (error.status === 401) {
                this.errorRefreshToken();
            } else {
                const errMsg = getErrorMessage(error);
                if (errMsg) {
                    this.commonUtilService.showToastWithError(errMsg);
                }
            }
        });
    }

    loadCampaigns() {
        this.zohoApiService.loadCampaigns().pipe(untilDestroyed(this)).subscribe((response: any) => {
            this.campaigns = response.items;
            this.updatePageSequence();
        }, error => {
            if (error.status === 401) {
                this.errorRefreshToken();
            } else {
                const errMsg = getErrorMessage(error);
                if (errMsg) {
                    this.commonUtilService.showToastWithError(errMsg);
                }
            }
        });
    }

    updatePageSequence() {
        if (this.generalSettings.allowExportAsContact === true) {
            this.exportType = 'contact';
        }

        if (this.generalSettings.allowExportAsLead === true) {
            this.exportType = 'lead';
        }
        this.pageArray = this.pages;
        Object.keys(this.pageArray).forEach((currentPage) => {
            if (this.pageArray[currentPage] === this.pageArray.exportType && (this.generalSettings.allowExportAsLead === false || this.generalSettings.allowExportAsContact === false)) {
                delete this.pageArray[currentPage];
            }

            if (this.generalSettings.promptForCampaigns === false && this.pageArray[currentPage] === this.pageArray.campaign) {
                delete this.pageArray[currentPage];
            }

            if (this.generalSettings.promptForLeadOwners === false && this.pageArray[currentPage] === this.pageArray.leadOwner) {
                delete this.pageArray[currentPage];
            }
        });
        // for (const currentPage in this.pageArray) {

        //     if (this.pageArray[currentPage] === this.pageArray.exportType && (this.generalSettings.allowExportAsLead === false || this.generalSettings.allowExportAsContact === false)) {
        //         delete this.pageArray[currentPage];
        //     }

        //     if (this.generalSettings.promptForCampaigns === false && this.pageArray[currentPage] === this.pageArray.campaign) {
        //         delete this.pageArray[currentPage];
        //     }

        //     if (this.generalSettings.promptForLeadOwners === false && this.pageArray[currentPage] === this.pageArray.leadOwner) {
        //         delete this.pageArray[currentPage];
        //     }
        // }
        // Reordering
        Object.entries(this.pageArray).forEach((key, index) => {
            this.pageArray[key[0]] = index;
        });
        this.onNext();
    }

    onNext() {
        this.currentPage++;
        if (this.data['contacts']) {
            if (this.currentPage === this.pageArray.campaign) {
                this.filterCampaigns = '';
            }
            this.executeCurrentStep();
        } else if (this.data['company']) {
            this.executeCurrentStepCompany();
        }
    }

    onPrevious() {
        if (this.data['contacts']) {
            let page = this.currentPage;
            page = page - 1;
            if (this.currentPage === this.pageArray.exportEmpty) {
                if (this.generalSettings.duplicateResolution !== 'prompt') {
                    page = this.pageArray.duplicates - 1;
                } else {
                    page = this.pageArray.duplicates;
                }
            }
            this.currentPage = page;
            this.executeCurrentStep();
        } else if (this.data['company']) {
            if (this.currentPage === this.pages.exportEmpty) {
                this.currentPage = this.pages.duplicates;
            } else {
                this.currentPage = this.currentPage--;
            }
            this.executeCurrentStepCompany();
        }
    }

    onClose() {
        this.isZohoComponentOpened = false;
        this.matDialogRef.close();
    }

    setExportType(type) {
        this.exportType = type;
        this.onNext();
    }
    executeCurrentStepCompany() {
        if (this.currentPage === this.initialPage) {
            this.showPreviousButton = false;
            this.showCloseButton = false;
        } else if (this.currentPage === this.pages.duplicates) {
            this.showPreviousButton = false;
            this.showNextButton = true;
            this.showCloseButton = false;
        } else if (this.currentPage === this.pages.export) {
            this.showPreviousButton = true;
            this.showNextButton = true;
            this.showRetryButton = false;
            this.showCloseButton = false;
            this.exportCompanies();
        } else if (this.currentPage === this.pages.exportEmpty) {
            this.showCancelButton = false;
            this.showPreviousButton = true;
            this.showNextButton = false;
            this.showCloseButton = true;
        } else if (this.currentPage === this.pages.exportFinished) {
            this.showCancelButton = false;
            this.showPreviousButton = false;
            this.showNextButton = false;
            this.showCloseButton = true;
        }
    }
    executeCurrentStep() {
        if (this.currentPage === this.pageArray.duplicates) {
            this.showCloseButton = false;
            this.validateDuplicates();
        } else if (this.currentPage === this.pageArray.export) {
            this.showCloseButton = false;
            this.export();
        } else if (this.currentPage === this.pageArray.exportEmpty) {
            if ((parseInt(this.pageArray.duplicates, 10) - 1) !== 0 && (parseInt(this.pageArray.duplicates, 10) - 1) !== undefined) {
                this.showPreviousButton = true;
            } else {
                this.showPreviousButton = false;
                this.showCancelButton = false;
            }
            this.showCloseButton = true;
            this.showNextButton = false;
        } else if (this.currentPage === this.pageArray.leadOwner || this.currentPage === this.pageArray.campaign) {
            this.showPreviousButton = (this.currentPage - 1) !== 0;
            this.showNextButton = true;
            this.showCloseButton = false;
        } else if (this.currentPage === this.pageArray.exportFinished) { // Last Page
            this.showNextButton = false;
            this.showPreviousButton = false;
            this.showCancelButton = false;
            this.showCloseButton = true;
        } else if (this.currentPage === this.pageArray.exportType) { // First Page
            this.showPreviousButton = false;
            this.showCloseButton = false;
            this.showNextButton = false;
        }
    }

    selectLeadOwner(id) {
        if (this.selectedLeadOwner === id) {
            this.selectedLeadOwner = null;
        } else {
            this.selectedLeadOwner = id;
        }
    }

    selectCampaign(cid) {
        const idx = this.selectedCampaigns.indexOf(cid);
        if (idx === -1) {
            this.selectedCampaigns.push(cid);
        } else {
            this.selectedCampaigns.splice(idx, 1);
        }
    }

    isCampaignSelected(cid) {
        return this.selectedCampaigns.indexOf(cid) !== -1;
    }

    validateCompanyDuplicates() {
        this.onNext();
        this.busyLabel = 'Validating Duplicates ...';
        this.busySublabel = 'Depending on the number of company, this could take a few minutes.';
        this.isBusy = true;
        const companyIds = this.companyData.map((item) => {
            if (typeof item === 'object') {
                return item.companyId.toString();
            } else {
                return item;
            }
        });

        const requestBody = {
            ids: companyIds,
            icpintel_job_uuid: this.icpIntelJobUUID
        };

        this.zohoApiService.validateCompaniesDuplicates(requestBody).pipe(untilDestroyed(this)).subscribe((resp: any) => {
            resp.items.forEach((item) => {
                if (item['duplicate'] === true) {
                    this.duplicateCompanyMatchIds.push(item);
                }
            });

            this.duplicateCompanyCount = this.duplicateCompanyMatchIds.length;
            if (this.duplicateCompanyCount === 0) {
                this.onNext();

            } else if (this.generalSettings.duplicateResolution !== 'prompt') {
                this.duplicateResolution = this.generalSettings.duplicateResolution;
                this.onNext();

            } else {
                this.isBusy = false;
            }
        }, (error) => {
            this.handleDuplicateError(error);
        });
    }
    validateDuplicates() {
        this.busyLabel = 'Validating Duplicates ...';
        this.busySublabel = 'Exporting your data, this will take just a minute...';
        this.isBusy = true;
        const matchIds = this.contacts.map((contact) => {
            if (typeof contact === 'object') {
                return contact.matchId;
            } else {
                return contact;
            }
        });
        this.zohoApiService.validateDuplicates(this.exportType, matchIds, this.icpIntelJobUUID).pipe(untilDestroyed(this)).subscribe((response: any) => {
            const dupes = [];
            let dupesCount = 0;
            const dupeLeads = [];
            const dupeContacts = [];
            let dupeLeadsCount = 0;
            let dupeContactsCount = 0;
            if ('leads' in response) {
                response.leads.forEach((item) => {
                    if (item['duplicate'] === true) {
                        dupesCount++;
                        dupeLeads.push(item);
                        dupes.push(item);
                    }
                });
                dupeLeadsCount = dupeLeads.length;
            }
            if ('contacts' in response) {
                response.contacts.forEach((item) => {
                    if (item['duplicate'] === true) {
                        dupesCount++;
                        dupeContacts.push(item);
                        dupes.push(item);
                    }
                });
                dupeContactsCount = dupeContacts.length;
            }
            this.duplicateLeadsCount = dupeLeadsCount;
            this.duplicateContactsCount = dupeContactsCount;
            this.duplicateLeadsMatchIds = dupeLeads;
            this.duplicateContactsMatchIds = dupeContacts;
            this.duplicateMatchIds = dupes;
            this.duplicateCount = dupesCount;
            if (dupesCount === 0) {
                this.onNext();
            } else {
                if (this.generalSettings.duplicateResolution !== 'prompt') {
                    this.duplicateResolution = this.generalSettings.duplicateResolution;
                    this.showPreviousButton = false;
                    this.showNextButton = false;
                    this.onNext();
                } else {
                    this.isBusy = false;
                    this.showNextButton = true;
                    if (this.pageArray.duplicates - 1 !== 0) {
                        this.showPreviousButton = true;
                    }
                }
            }
        }, (error) => {
            this.handleDuplicateError(error);
        });
    }

    getByMatchId(id, name) {
        let Name;
        if (this.contacts.filter((item) => item.matchId === id).length > 0) {
            Name = this.contacts.filter((item) => item.matchId === id)[0][name];
        } else {
            Name = '';
        }
        return Name;
    }
    exportCompanies() {
        const toBeExported = this.companyData.map((item) => {
            if (typeof item === 'object') {
                return item.companyId.toString();
            } else {
                return item.toString();
            }
        });
        // Skip all duplicates
        if (this.duplicateCompanyMatchIds && this.duplicateResolution === 'skip') {
            this.duplicateCompanyMatchIds.forEach((item) => {
                this.commonUtilService.removeMatchId(toBeExported, item, 'company');
            });
        }

        if (toBeExported.length === 0) {
            this.isBusy = false;
            this.currentPage = this.pages.exportEmpty;
            this.executeCurrentStepCompany();
        } else {
            this.exportedCompanyCount = toBeExported.length;
            this.busyLabel = 'Exporting ...';
            this.busySublabel = 'Depending on the number of companies, this could take a few minutes.';
            this.isBusy = true;
            const requestBody = {
                ids: toBeExported,
                duplicate_resolution: this.duplicateResolution === 'skip' ? '' : this.duplicateResolution,
                icpintel_job_uuid: this.icpIntelJobUUID
                // company_ids: toBeExported,
                // verified: this.data['verified'],
                // is_international: this.data['international']
            };

            this.zohoApiService.exportCompany(requestBody).pipe(untilDestroyed(this)).subscribe((response: any) => {
                this.failedCompany = response.items;
                this.failedCompany = lodashFilter(this.failedCompany, ({ statusCode }) => every([!includes([200, 201], statusCode)]));

                // Skip all failed company
                if (this.failedCompany && this.failedCompany.length > 0) {
                    this.failedCompany.forEach((failedCompay) => {
                        const companyId = toBeExported.filter((id) => failedCompay?.companyId === id)[0];
                        this.commonUtilService.removeMatchId(toBeExported, companyId, 'company');
                    });
                }
                if (toBeExported.length > 0) {
                    this.authenticationService.updateLoggedUserData();
                    const exportedIds = toBeExported.map((item) => parseInt(item, 10));
                    this.requestBodyService.changeCompanyRevealMatchesID({ 'type': 'zoho', 'data': exportedIds });
                }
                this.isBusy = false;
                this.onNext();
            }, (error) => {
                const errMsg = getErrorMessage(error);
                if (errMsg) {
                    this.exportError = errMsg;
                    this.showRetryButton = true;
                } else {
                    this.exportError = this.commonUtilService.setRevealError(error.status, error.message);
                }

                this.currentPage = this.pages.exportFailed;
                this.showNextButton = false;
                this.showPreviousButton = false;
                this.isBusy = false;
            });
        }
    }
    export() {
        const toBeExported = this.contacts.map((contact) => {
            if (typeof contact === 'object') {
                return contact.matchId;
            } else {
                return contact;
            }
        });
        // Skip all duplicates
        if (this.duplicateResolution === 'skip') {
            if (typeof this.duplicateMatchIds === 'object') {
                for (const key in this.duplicateLeadsMatchIds) {
                    if (this.duplicateLeadsMatchIds.hasOwnProperty(key)) {
                        this.commonUtilService.removeMatchId(toBeExported, this.duplicateLeadsMatchIds[key]);
                    }
                }
                for (const key in this.duplicateContactsMatchIds) {
                    if (this.duplicateContactsMatchIds.hasOwnProperty(key)) {
                        this.commonUtilService.removeMatchId(toBeExported, this.duplicateContactsMatchIds[key]);
                    }
                }
            } else {
                this.duplicateMatchIds.forEach((item) => {
                    this.commonUtilService.removeMatchId(toBeExported, item.key);
                });
            }
        }
        if (toBeExported.length === 0) {
            this.isBusy = false;
            this.currentPage = this.pageArray.exportEmpty;
            this.executeCurrentStep();
            return;
        }
        // this.busyLabel = 'Exporting ...';
        this.busyLabel = '';
        this.busySublabel = 'Exporting your data, this will take just a minute...';
        this.isBusy = true;
        this.exportedContactCount = toBeExported.length;
        this.zohoApiService.export(this.exportType, this.selectedLeadOwner, this.selectedCampaigns, this.duplicateResolution, toBeExported, this.icpIntelJobUUID)
        .pipe(untilDestroyed(this))
        .subscribe((response: any) => {
            const failedContactsArray = response.items;
            this.failedContacts = lodashFilter(failedContactsArray, ({ statusCode }) => every([!includes([200, 201], statusCode)]));
            failedContactsArray.forEach((contact) => {
                this.duplicateContactsMatchIds.forEach((contacts) => {
                    if (contact.matchId === contacts.match_id) {
                        const dFailedContact = new FailedContactModel();
                        dFailedContact.contactName = contact.name ? contact.name : this.getByMatchId(contact.matchId, 'name');
                        dFailedContact.companyName = contact.companyName ? contact.companyName : this.getByMatchId(contact.matchId, 'companyName');
                        dFailedContact.matchId = contact.matchId;
                        this.commonUtilService.removeMatchId(toBeExported, contact);
                        this.duplicateFailedContacts.push(dFailedContact);
                    }
                });
            });
            this.exportedContactCount = toBeExported.length;

            this.isBusy = false;
            this.onNext();
            if (toBeExported.length > 0) {
                this.requestBodyService.changeRevealMatchesID({ 'type': 'zoho', 'data': toBeExported });
            }
        }, (error) => {
            const errMsg = getErrorMessage(error);
            if (errMsg) {
                this.exportError = errMsg;
                this.showRetryButton = true;
            } else {
                this.exportError = this.commonUtilService.setRevealError(error.status, error.message);
            }

            this.currentPage = this.pageArray.exportFailed;
            this.showNextButton = false;
            this.showPreviousButton = false;
            this.isBusy = false;
        });
    }

    onRetryExport() {
        if (this.data['contacts']) {
            this.currentPage = this.pageArray.export;
            this.executeCurrentStep();
        } else if (this.data['company']) {
            this.currentPage = this.pages.export;
            this.executeCurrentStepCompany();
        }
    }

    private handleDuplicateError(error: any): void {
        // Stop any loaders
        this.isBusy = false;

        // Handle auth-related errors first
        const invalidGrant: boolean = error?.status === 400 && error?.error?.errors?.[0]?.code === 'invalid_grant';
        if (error?.status === 401 || invalidGrant) {
            this.errorRefreshToken();
            return;
        }

        // Extract a user-friendly error message and show toast
        const errorMessage: string = getErrorMessage(error, false, 'An unexpected error occurred.');
        this.commonUtilService.showToastWithError(errorMessage);
        this.onClose();
    }
}

export class FailedContactModel {
    matchId: string;
    contactName: string;
    companyName: string;
    errorMessage: string;
}
